// Relative paths, not the "@assets" alias -- same reasoning as coreValueBadgeArt.ts and rewardArtwork.ts:
// "@assets/*" isn't in jest's moduleNameMapper, and this module's test needs the real imports. One
// static import per file (never a dynamic `require`) so webpack fingerprints every asset.
import flameFlint from "../../../../assets/images/flameBadges/flame-flint.png";
import flameSpark from "../../../../assets/images/flameBadges/flame-spark.png";
import flameEmber from "../../../../assets/images/flameBadges/flame-ember.png";
import flameKindle from "../../../../assets/images/flameBadges/flame-kindle.png";
import flameBlaze from "../../../../assets/images/flameBadges/flame-blaze.png";
import flameTorch from "../../../../assets/images/flameBadges/flame-torch.png";
import flameBonfire from "../../../../assets/images/flameBadges/flame-bonfire.png";
import flameFurnace from "../../../../assets/images/flameBadges/flame-furnace.png";
import flameInferno from "../../../../assets/images/flameBadges/flame-inferno.png";
import flameWildfire from "../../../../assets/images/flameBadges/flame-wildfire.png";
import flameFirestorm from "../../../../assets/images/flameBadges/flame-firestorm.png";
import flameStar from "../../../../assets/images/flameBadges/flame-star.png";
import flameSupernova from "../../../../assets/images/flameBadges/flame-supernova.png";

/**
 * Bundled flame-ladder badge artwork (176x176 PNG, transparent), keyed by the api's own EXACT
 * `badge_definitions.code` (api `migrations/20260807090100_Insert-BadgeDefinitions-FlameTenure.js`).
 *
 * Keyed on the code verbatim rather than derived from it, unlike `coreValueBadgeArt.ts`: flame codes are
 * static strings, identical in every environment, so there is no id to resolve and no reason to
 * transform the key. The map IS the mapping -- a lookup either hits or it doesn't.
 *
 * Code-to-art order was verified against the Figma board (`Flame_L`, `9657:1286`) rather than assumed:
 * six icons are unambiguous and each sits exactly where its code does in the ladder -- torch 5th,
 * campfire 6th (bonfire), brick oven 7th (furnace), meteors 10th (firestorm), star 11th, burst 12th
 * (supernova) -- and the source images' generation timestamps ascend in the same order. That check
 * matters: three of the five core-value boards in the same file were mislabelled.
 *
 * The board was revised (pavel, 2026-09-23) to 13 icons: icon 1 (the grey crystal, previously the Spark art) is
 * now the dedicated Flint emblem below -- the not-started state's art, `current_tier: null` -- and icons
 * 2/3 got new Spark/Ember art. Icons 4-13 are this same twelve-entry ladder, Kindle through Supernova,
 * unchanged in meaning and order.
 */
const FLAME_BADGE_ART: Record<string, string> = {
  flame_spark: flameSpark,
  flame_ember: flameEmber,
  flame_kindle: flameKindle,
  flame_blaze: flameBlaze,
  flame_torch: flameTorch,
  flame_bonfire: flameBonfire,
  flame_furnace: flameFurnace,
  flame_inferno: flameInferno,
  flame_wildfire: flameWildfire,
  flame_firestorm: flameFirestorm,
  flame_star: flameStar,
  flame_supernova: flameSupernova,
};

/**
 * Resolves a flame badge's `code` to its bundled artwork, or `null` for any code that is not one of the
 * twelve flame codes (every other category, or an unknown flame code). Never throws.
 *
 * `Object.prototype.hasOwnProperty` rather than a bare index: a bare `FLAME_BADGE_ART[code]` would
 * return an inherited member for codes like `"constructor"` or `"toString"`, handing the caller a
 * function where it expects an asset URL.
 *
 * @param code - a badge's own `code`, e.g. `"flame_torch"`.
 * @returns the bundled asset URL, or `null`.
 */
export const resolveFlameBadgeArt = (code: string): string | null =>
  Object.prototype.hasOwnProperty.call(FLAME_BADGE_ART, code) ? FLAME_BADGE_ART[code] : null;

/**
 * The not-started emblem (`current_tier: null`, shown as "Flint") -- Figma board icon 1, the grey
 * crystal. Deliberately NOT a `badge_definitions.code` and NOT a member of `FLAME_BADGE_ART`: the
 * catalog has no Flint badge (there is nothing to earn to become "not started"), so this is exported as
 * its own constant rather than added to the code-keyed map, and `resolveFlameBadgeArt` never returns it.
 */
export const FLINT_EMBLEM_ART = flameFlint;
